<template>
    <div class="container-fluid p-0 service-session-component">
        <Loader v-if="loading" />
        <b-row v-else>
            <b-form class="m-md-3 container-fluid">
                <b-card-header header-bg-variant="primary" header-text-variant="light" class="rounded">
                    {{$t('device_service_text')}}
                </b-card-header>
                <b-button class="mt-2" type="button" variant="primary" @click="sendCommand" :disabled="commandSentDisabled">
                    {{$t('start_debug_session')}}
                </b-button>
                <b-alert :show="showSuccessAlert" variant="success">{{$t('command_was_send')}}</b-alert>
                <b-alert :show="commandError" variant="danger" class="service-command-error">{{$t('service_command_error')}}</b-alert>
                <b-alert :show="loadError" variant="danger" class="service-read-error">
                    {{$t('service_load_error')}}
                    <b-button type="button" variant="outline-danger" :disabled="reading" @click="updateServiceText">{{$t('retry')}}</b-button>
                </b-alert>
                <b-form-group label-for="service_data" :label="$t('device_additional_info_from_data.service_data')" class="m-md-0 container-fluid">
                    <b-form-input id="service_data" :value="serviceText.date" disabled />
                </b-form-group>
                <b-form-group label-for="service_text" :label="$t('device_additional_info_from_data.service_text')" class="m-md-0 container-fluid">
                    <b-button class="mt-2" type="button" variant="primary" @click="downloadText" :disabled="!serviceText.text">{{$t('seve_to_file')}}</b-button>
                    <b-form-textarea id="service_text" :value="serviceText.text" disabled style="height: 300px" />
                </b-form-group>
            </b-form>
        </b-row>
    </div>
</template>

<script>
    import axios from "axios";
    import Loader from "../Common/Loader.vue";
    import { RequestGuard, REQUEST_TIMEOUT_MS } from "../../utils/RequestGuard";
    import { watchPageVisibility } from "../../utils/PageVisibility";

    const POLL_MS = 15000;
    const COMMAND_COOLDOWN_MS = 120000;
    const RECENT_SESSION_MS = 15 * 60 * 1000;
    const emptyServiceText = () => ({ text: null, session: null, date: null });

    export default {
        name: "ServiceText",
        components: { Loader },
        props: { id: { type: Number, required: true } },
        data() {
            return {
                loading: true, reading: false, loadError: false, sending: false,
                commandError: false, showSuccessAlert: false,
                serviceText: emptyServiceText(), serviceDate: null,
                clock: Date.now(), cooldownUntil: 0,
                pollTimer: null, cooldownTimer: null,
                context: 0, disposed: false, active: false,
            };
        },
        computed: {
            validDeviceId() { return Number.isInteger(this.id) && this.id > 0 && this.id <= 999999; },
            commandSentDisabled() {
                return !this.active || this.loading || this.loadError || !this.validDeviceId || this.sending
                    || this.clock < this.cooldownUntil
                    || (this.serviceDate !== null && this.clock < this.serviceDate + RECENT_SESSION_MS);
            },
        },
        created() {
            this.readGuard = new RequestGuard();
            this.unsubscribeVisibility = watchPageVisibility(this.visibilityChanged);
        },
        mounted() { this.resume(); },
        activated() { this.resume(); },
        deactivated() { this.pause(); },
        beforeDestroy() {
            this.disposed = true;
            this.context++;
            this.pause();
            this.unsubscribeVisibility();
        },
        watch: {
            id() {
                const wasActive = this.active;
                this.context++;
                this.pause();
                this.serviceText = emptyServiceText(); this.serviceDate = null;
                this.loading = true; this.loadError = false; this.sending = false;
                this.commandError = false; this.showSuccessAlert = false; this.cooldownUntil = 0;
                if (wasActive) this.resume();
            },
        },
        methods: {
            resume() {
                if (this.disposed) return;
                this.active = true;
                this.visibilityChanged();
            },
            pause() {
                this.active = false;
                this.stopTimers();
                this.readGuard.invalidate();
                this.reading = false;
            },
            stopTimers() {
                clearInterval(this.pollTimer); this.pollTimer = null;
                clearTimeout(this.cooldownTimer); this.cooldownTimer = null;
            },
            visibilityChanged() {
                if (this.disposed || !this.active) return;
                this.clock = Date.now();
                if (document.hidden) {
                    this.stopTimers(); this.readGuard.invalidate(); this.reading = false;
                    return;
                }
                if (this.pollTimer === null) this.pollTimer = setInterval(this.updateServiceText, POLL_MS);
                this.armCooldown();
                this.updateServiceText();
            },
            armCooldown() {
                clearTimeout(this.cooldownTimer); this.cooldownTimer = null;
                const remaining = this.cooldownUntil - Date.now();
                if (remaining > 0 && this.active && !document.hidden) {
                    this.cooldownTimer = setTimeout(() => {
                        this.clock = Date.now(); this.cooldownTimer = null;
                    }, remaining);
                }
            },
            async updateServiceText() {
                if (this.disposed || !this.active || document.hidden) return;
                this.clock = Date.now();
                if (!this.validDeviceId) { this.loading = false; this.loadError = true; return; }
                const request = this.readGuard.begin();
                if (request === null) return;
                this.reading = true;
                try {
                    const response = await axios.get('api/v1/devices/' + this.id + '/coordinates', this.readGuard.config(request));
                    if (!this.readGuard.isCurrent(request)) return;
                    const message = response.data.serviceMessage;
                    if (message != null && (typeof message !== 'object' || Array.isArray(message))) throw new Error('Invalid service telemetry');
                    const date = message && message.date;
                    const timestamp = typeof date === 'string' ? new Date(date.replace(' ', 'T')).getTime() : null;
                    if (date != null && (timestamp === null || !Number.isFinite(timestamp))) throw new Error('Invalid service date');
                    if (message && message.message != null && typeof message.message !== 'string') throw new Error('Invalid service text');
                    this.serviceText = message ? { text: message.message ?? null, session: message.session ?? null, date: date ?? null } : emptyServiceText();
                    this.serviceDate = timestamp;
                    this.loadError = false;
                } catch (_) {
                    if (this.readGuard.isCurrent(request)) this.loadError = true;
                } finally {
                    if (this.readGuard.isCurrent(request)) {
                        this.readGuard.finish(request); this.reading = false; this.loading = false;
                    }
                }
            },
            async sendCommand() {
                this.clock = Date.now();
                if (this.disposed || document.hidden || this.commandSentDisabled) return;
                const context = this.context;
                const deviceId = this.id;
                this.sending = true; this.commandError = false; this.showSuccessAlert = false;
                try {
                    const response = await axios.post('api/v1/devices/' + deviceId + '/commands/startServiceSession', undefined, { timeout: REQUEST_TIMEOUT_MS });
                    if (this.disposed || context !== this.context) return;
                    if (response.data?.status !== 'ok') throw new Error('Service command failed');
                    this.showSuccessAlert = true;
                    this.clock = Date.now(); this.cooldownUntil = this.clock + COMMAND_COOLDOWN_MS;
                    this.armCooldown();
                } catch (_) {
                    if (!this.disposed && context === this.context) this.commandError = true;
                } finally {
                    if (!this.disposed && context === this.context) this.sending = false;
                }
            },
            downloadText() {
                if (this.disposed || !this.serviceText.text) return;
                const url = window.URL.createObjectURL(new Blob([this.serviceText.text], { type: 'text/plain;charset=utf-8' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'service_text_' + (this.serviceText.date || 'undated').replace(/[^0-9A-Za-z_-]/g, '_') + '.txt';
                document.body.appendChild(link);
                try { link.click(); }
                finally { link.remove(); window.URL.revokeObjectURL(url); }
            },
        },
    };
</script>

<style scoped>
</style>
